import formatName from 'I18n/utils/formatName';
import { fromJS, Map as ImmutableMap } from 'immutable';
import { createContext, useContext, useReducer } from 'react';
import { hsToStripePaymentMethod } from '../utils/paymentMethods';
import { jsx as _jsx } from "react/jsx-runtime";
export function init({
  session,
  formStateOverride
}) {
  const checkoutSession = session.toJS();
  const prefillData = checkoutSession.prefillData || {};
  const customerEmail = checkoutSession.customerEmail || prefillData.email || '';
  const shippableCountries = checkoutSession.shippableCountries;
  const onlyShippingCountryOrEmptyString = shippableCountries && shippableCountries.length === 1 ? shippableCountries[0].toLowerCase() : '';
  const paymentMethods = new Set(checkoutSession.paymentMethods.map(paymentMethodConfig => paymentMethodConfig.paymentMethod));
  const hasCardPaymentMethod = paymentMethods.has('CARD');
  const customerData = {
    firstName: prefillData.firstName || '',
    lastName: prefillData.lastName || ''
  };
  const fullName = customerData.firstName && customerData.lastName ? formatName({
    firstName: customerData.firstName,
    lastName: customerData.lastName
  }) : '';
  const paymentMethod = hasCardPaymentMethod ? 'CARD' : [...paymentMethods][0];
  const initialFormState = Object.assign({
    bankInfo: {
      accountHolderName: '',
      accountHolderType: 'INDIVIDUAL',
      transactionType: 'BUSINESS'
    },
    billingAddress: {
      fullName,
      address: '',
      address2: '',
      city: '',
      country: onlyShippingCountryOrEmptyString,
      postalCode: '',
      state: ''
    },
    billingSameAsShipping: !!shippableCountries.length,
    collectPaymentMethodOnFileAcknowledged: false,
    clickToCancelAcknowledged: false,
    customData: {},
    discountCode: '',
    discountCodeError: false,
    email: customerEmail,
    isCheckoutSubmitPending: false,
    isDiscountCodeApplied: false,
    loading: false,
    merchantTermsOfServiceAcknowledged: false,
    pageError: false,
    paymentMethod,
    stripePaymentMethod: hsToStripePaymentMethod(paymentMethod),
    phoneNumbers: {},
    shippingAddress: Object.assign({}, customerData, {
      address: '',
      address2: '',
      city: '',
      country: onlyShippingCountryOrEmptyString,
      postalCode: '',
      state: ''
    }),
    stripeError: null,
    stripeValidationError: false
  }, formStateOverride);
  return fromJS(initialFormState);
}
export function reducer(state, action) {
  switch (action.type) {
    case 'setPaymentMethod':
      return state.set('paymentMethod', action.data.value).set('bankInfo', fromJS({
        accountHolderName: '',
        accountHolderType: 'INDIVIDUAL',
        transactionType: 'BUSINESS'
      }));
    case 'setStripePaymentMethod':
      return state.set('stripePaymentMethod', action.data.value);
    case 'setShippingAddressField':
      {
        let updatedState = state;
        const newCountrySelected = action.data.name === 'country' && state.getIn(['shippingAddress', 'country']) !== action.data.value;
        if (newCountrySelected) {
          updatedState = updatedState.setIn(['shippingAddress', 'state'], '');
        }
        if (state.get('billingSameAsShipping')) {
          if (newCountrySelected) {
            updatedState = updatedState.setIn(['billingAddress', 'state'], '');
          }
          if (action.data.name === 'firstName' || action.data.name === 'lastName') {
            updatedState = updatedState.setIn(['billingAddress', 'fullName'], formatName({
              firstName: action.data.name === 'firstName' ? action.data.value : state.getIn(['shippingAddress', 'firstName']),
              lastName: action.data.name === 'lastName' ? action.data.value : state.getIn(['shippingAddress', 'lastName'])
            }));
          } else {
            updatedState = updatedState.setIn(['billingAddress', action.data.name], action.data.value);
          }
        }
        return updatedState.setIn(['shippingAddress', action.data.name], action.data.value);
      }
    case 'setBillingAddressField':
      return state.setIn(['billingAddress', action.data.name], action.data.value);
    case 'setBillingSameAsShipping':
      return state.set('billingSameAsShipping', action.data.value).update('billingAddress', billingAddress => action.data.value ? billingAddress.reduce((acc, __value, key) => {
        // Copy values from shippingAddress if the keys match
        if (state.hasIn(['shippingAddress', key])) {
          return acc.set(key, state.getIn(['shippingAddress', key]));
        }
        return acc;
      }, ImmutableMap()).set('fullName', formatName({
        firstName: state.getIn(['shippingAddress', 'firstName']),
        lastName: state.getIn(['shippingAddress', 'lastName'])
      })) : billingAddress);
    case 'setBankInfoField':
      return state.setIn(['bankInfo', action.data.name], action.data.value);
    case 'setField':
      return state.setIn(Array.isArray(action.data.propertyPath) ? action.data.propertyPath : [action.data.propertyPath], action.data.value);
    case 'setStripeError':
      return state.set('stripeError', action.data);
    case 'resetStripeError':
      return state.set('stripeError', null);
    case 'setStripeValidationError':
      return state.set('stripeValidationError', true);
    case 'resetStripeValidationError':
      return state.set('stripeValidationError', false);
    case 'setDiscountCode':
      return state.set('discountCode', action.data);
    case 'setDiscountCodeError':
      return state.set('discountCodeError', action.data);
    case 'setMerchantTermsOfServiceAcknowledged':
      return state.set('merchantTermsOfServiceAcknowledged', action.data);
    case 'setCollectPaymentMethodOnFileAcknowledged':
      return state.set('collectPaymentMethodOnFileAcknowledged', action.data);
    case 'setClickToCancelAcknowledged':
      return state.set('clickToCancelAcknowledged', action.data);
    case 'setIsDiscountCodeApplied':
      return state.set('isDiscountCodeApplied', action.data);
    case 'setIsCheckoutSubmitPending':
      return state.set('isCheckoutSubmitPending', action.data.value);
    case 'setPhoneNumbers':
      return state.set('phoneNumbers', fromJS(action.data));
    case 'setPageError':
      return state.set('pageError', action.data);
    default:
      return state;
  }
}
export const PaymentFormContext = /*#__PURE__*/createContext({
  formState: ImmutableMap(),
  checkoutSession: ImmutableMap(),
  dispatch: () => {}
});

/**
 * Context for managing the state of the form
 *
 * @param {node || element || node[] || element[]} children     children that will be rendered to display the form
 * @param {ImmutableMap} checkoutSession                        immutable map that provides the data to initialize the form
 * @returns {node || element || node[] || element[]}            children rendered inside of the context provider
 */
export const PaymentFormContextProvider = ({
  children,
  checkoutSession,
  formStateOverride
}) => {
  const initialArg = {
    session: checkoutSession,
    formStateOverride
  };
  const [state, dispatch] = useReducer(reducer, initialArg, init);
  const contextValue = {
    formState: state,
    dispatch,
    checkoutSession
  };
  return /*#__PURE__*/_jsx(PaymentFormContext.Provider, {
    value: contextValue,
    children: children
  });
};

/**
 * a hook to access PaymentFormContext
 *
 * @returns {Object<{ formState, checkoutSession, dispatch }>}    object that contains formState, dispatch, and checkoutSession
 * formState                                                      object that contains the state for the checkout form
 * checkoutSession                                                the input to `PaymentFormContextProvider`
 * dispatch                                                       dispatcher function to update formState
 */
export const usePaymentFormContext = () => {
  const context = useContext(PaymentFormContext);
  if (context === undefined) {
    throw new Error('Please provide PaymentFormContext in order to use this hook');
  }
  return context;
};